 
import { put, takeLatest, takeEvery } from 'redux-saga/effects';
import types from 'redux/types';
import Services from 'services';
import { getNextDataRefetch, isResponseSuccess, ScrollTopPosition } from 'helpers';
import hooks from 'hooks';
import {
  SAVE_DIALOG_URL,
  DELETE_DIALOG_URL,
  LOAD_DIALOG_DETAIL_URL,
  SAVE_DIALOG_CONTENT_URL,
  DELETE_DIALOG_CONTENT_URL,
  UPLOAD_DIALOG_FILE_URL,
  SOFT_DELETE_DIALOG_CONTENT_URL,
} from 'consts/api';
import { showError } from 'helpers/handleError';
import { TIME_OUT_CLOSE_TOAST } from 'consts/data';
import { ITEM_PER_PAGE_RESPONDER } from 'consts/filters';
import ResponderServices from 'services/ResponderServices';
import { cloneDeep, toString } from 'lodash';
import { AppRoutes } from 'consts/routesPath';
import ResponderModel from 'models/responder.model';

const { Notification } = hooks;

const {
  REQUEST_GET_LIST_DIALOG,
  REQUEST_GET_LIST_DIALOG_FAILED,
  REQUEST_GET_LIST_DIALOG_SUCCESS,
  REQUEST_SWITCH_RESPONDER,
  REQUEST_SWITCH_RESPONDER_FAILED,
  REQUEST_SWITCH_RESPONDER_SUCCESS,
  REQUEST_SAVE_DIALOG,
  REQUEST_SAVE_DIALOG_SUCCESS,
  REQUEST_SAVE_DIALOG_FAILED,
  REQUEST_DELETE_RESPONDER,
  REQUEST_DELETE_RESPONDER_SUCCESS,
  REQUEST_DELETE_RESPONDER_FAILED,
  REQUEST_ADD_RESPONDER,
  REQUEST_ADD_RESPONDER_SUCCESS,
  REQUEST_ADD_RESPONDER_FAILED,
  REQUEST_GET_RESPONDER_DETAIL,
  REQUEST_GET_RESPONDER_DETAIL_SUCCESS,
  REQUEST_GET_RESPONDER_DETAIL_FAILED,
  REQUEST_SAVE_RESPONDER_CONTENT,
  REQUEST_SAVE_RESPONDER_CONTENT_SUCCESS,
  REQUEST_SAVE_RESPONDER_CONTENT_FAILED,
  REQUEST_UPLOAD_NODE,
  REQUEST_UPLOAD_NODE_FAILED,
  REQUEST_UPLOAD_NODE_SUCCESS,
  REQUEST_DELETE_RESPONDER_CONTENT,
  REQUEST_DELETE_RESPONDER_CONTENT_SUCCESS,
  REQUEST_DELETE_RESPONDER_CONTENT_FAILED,
  REQUEST_GET_LIST_DIALOG_TEMPLATE,
  REQUEST_GET_LIST_DIALOG_TEMPLATE_FAILED,
  REQUEST_GET_LIST_DIALOG_TEMPLATE_SUCCESS,
  REQUEST_SET_DID_GROUP_RESPONDER,
  REQUEST_SET_DID_GROUP_RESPONDER_SUCCESS,
  REQUEST_SET_DID_GROUP_RESPONDER_FAILED,
  REQUEST_LOAD_MORE_RESPONDER,
  REQUEST_LOAD_MORE_RESPONDER_SUCCESS,
  REQUEST_LOAD_MORE_RESPONDER_FAILED,
  REQUEST_FILTER_RESPONDER,
  REQUEST_FILTER_RESPONDER_SUCCESS,
  REQUEST_FILTER_RESPONDER_FAILED,
  REQUEST_REFETCH_RESPONDER_FAILED,
} = types;

const checkIsLastResponder = (list = []) => {
  return list.length <= 0 || list.length < ITEM_PER_PAGE_RESPONDER;
};

// refetch dialog with paging
function* refetchDialog({ published, filters = {} }) {
  const parseFilters = cloneDeep(filters);
  const page = parseFilters.page + 1;
  parseFilters.status = parseFilters?.status?.value;
  parseFilters.contactGroupName = parseFilters?.contactGroupName?.name;

  try {
    let listRequest = [];
    for (let i = 0; i < page; i++) {
      listRequest = [
        ...listRequest,
        ResponderServices.getListDialogs({ published, filters: { ...parseFilters, page: i } }),
      ];
    }

    const response = yield Promise.all(listRequest) || [];
    const [nextData, isLast] = getNextDataRefetch(response, ITEM_PER_PAGE_RESPONDER);
    yield put({
      type: REQUEST_FILTER_RESPONDER_SUCCESS,
      data: nextData,
      published,
      isLast,
    });
  } catch (error) {
    yield put({ type: REQUEST_REFETCH_RESPONDER_FAILED, error });
    showError(toString(error));
  }
}

function* getListDialog({ filters = {} }) {
  try {
    // parse data to request
    const parseFilters = cloneDeep(filters);

    const res = yield ResponderServices.getListDialogs({ published: true, filters: parseFilters });
    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'object') {
        yield put({
          type: REQUEST_GET_LIST_DIALOG_SUCCESS,
          data: res?.data || [],
          isLast: checkIsLastResponder(res?.data || []),
        });
      }
      if (typeof res.data === 'string') {
        const message = res.data.toLowerCase();
        if (message === 'fail') {
          showError(message);
          yield put({
            type: REQUEST_GET_LIST_DIALOG_FAILED,
            error: message,
          });
        }
      }
    }
  } catch (error) {
    showError(error);
    yield put({
      type: REQUEST_GET_LIST_DIALOG_FAILED,
      error,
    });
  }
}

function* getListDialogTemplate(data) {
  const { filters = {} } = data;
  try {
    // parse data to request
    const parseFilters = cloneDeep(filters);
    delete parseFilters?.status;
    delete parseFilters?.contactGroupName;

    const res = yield ResponderServices.getListDialogs({ published: false, filters: parseFilters });
    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'object') {
        yield put({
          type: REQUEST_GET_LIST_DIALOG_TEMPLATE_SUCCESS,
          dataTemplate: res?.data || [],
          isLast: checkIsLastResponder(res?.data || []),
        });
      }
      if (typeof res.data === 'string') {
        const message = res.data.toLowerCase();
        if (message === 'fail') {
          showError(message);
          yield put({
            type: REQUEST_GET_LIST_DIALOG_TEMPLATE_FAILED,
            error: message,
          });
        }
      }
    }
  } catch (error) {
    showError(error);
    yield put({
      type: REQUEST_GET_LIST_DIALOG_TEMPLATE_FAILED,
      error,
    });
  }
}

function* switchResponder(data) {
  const { id, filters, published } = data;
  try {
    data.type = data.originalType;
    delete data.originalType;

    const res = yield ResponderServices.switchResponder(data);
    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'object') {
        // re-get list responder
        yield refetchDialog({ published, filters });
        yield put({
          type: REQUEST_SWITCH_RESPONDER_SUCCESS,
          data: res.data,
          id,
        });
        Notification('success', 'Changed status Successfully!', '', TIME_OUT_CLOSE_TOAST);
      } else {
        showError(res.data);
        yield put({
          type: REQUEST_SWITCH_RESPONDER_FAILED,
          error: res.data,
          id,
        });
      }
    }
  } catch (error) {
    let responseMessage = ((error.response || {}).data || {}).body;
    if (responseMessage != undefined) showError(responseMessage);
    else showError(error);
    yield put({
      type: REQUEST_SWITCH_RESPONDER_FAILED,
      error,
      id,
    });
  }
}

function* updateDialog(data) {
  const { body, callbacks } = data;
  try {
    const res = yield Services.post(
      SAVE_DIALOG_URL,
      ResponderModel.parseBodyToSaveResponderDetail(body)
    );
    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'object') {
        Notification('success', 'Updated responder successfully!', '', TIME_OUT_CLOSE_TOAST);
        yield put({
          type: REQUEST_SAVE_DIALOG_SUCCESS,
          dialogDetail: res.data,
        });
        callbacks && callbacks.onSuccess();
      } else {
        showError(res.data);
        yield put({
          type: REQUEST_SAVE_DIALOG_FAILED,
          error: res.data,
        });
        callbacks && callbacks.onFailed();
      }
    }
  } catch (error) {
    showError(error);
    yield put({
      type: REQUEST_SAVE_DIALOG_FAILED,
      error,
    });
    callbacks && callbacks.onFailed();
  }
}

function* deleteResponder(data) {
  const { id, setSubmitting, toggle, isResponderTemplate, filters } = data;
  try {
    const res = yield Services.post(`${DELETE_DIALOG_URL}?id=${id}`);
    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'number' && res.data === 1) {
        yield refetchDialog({ published: !isResponderTemplate, filters });
        yield put({
          type: REQUEST_DELETE_RESPONDER_SUCCESS,
          id,
          isResponderTemplate,
        });
        Notification('success', 'Deleted responder successfully!', '', TIME_OUT_CLOSE_TOAST);
        setSubmitting(false);
        toggle();
      } else {
        yield put({
          type: REQUEST_DELETE_RESPONDER_FAILED,
          error: res.data,
          id,
        });
        showError('Something went wrong!');
        setSubmitting(false);
        toggle();
      }
    }
  } catch (error) {
    showError(error);
    setSubmitting(false);
    toggle();
    yield put({
      type: REQUEST_DELETE_RESPONDER_FAILED,
      error,
      id,
    });
  }
}

function* createResponder(data) {
  const {
    contactGroupId,
    contactGroupName,
    history,
    setSubmitting,
    isNotDialog,
    isCreateTemplate,
    title,
    enableTemplate,
    idResponderTemplate,

    /**
     * Param campaign: use to save campaign that clicked by user from -
     * history campaign. TA-1649
     * */
    campaign = {},
    callbacks,
  } = data;
  try {
    if (isCreateTemplate) {
      const res = yield Services.post(SAVE_DIALOG_URL, { published: false, title });
      if (isResponseSuccess(res.status)) {
        if (typeof res.data === 'object') {
          const id = res.data.id;
          Notification(
            'success',
            'Build new responder template successfully!',
            '',
            TIME_OUT_CLOSE_TOAST
          );
          yield put({ type: REQUEST_ADD_RESPONDER_SUCCESS });
          history.push({
            pathname: AppRoutes.RespondersDetailWithId(id),
            state: {
              bestCampaign: campaign,
            },
          });
        } else {
          showError(res.data);
          yield put({
            type: REQUEST_ADD_RESPONDER_FAILED,
            error: res.data,
          });
        }
      }
      return;
    }

    let res;
    if (enableTemplate) {
      res = yield ResponderServices.createResponder({
        contactGroupId,
        contactGroupName,
        responderTemplate: idResponderTemplate,
        published: true,
      });
    } else {
      res = yield ResponderServices.createResponder({
        contactGroupId,
        contactGroupName,
        published: true,
      });
    }

    // If action is not executed through modal / dialog
    if (isNotDialog) {
      if (isResponseSuccess(res.status)) {
        if (typeof res.data === 'object') {
          const id = res.data.id;
          Notification('success', 'Build new responder successfully!', '', TIME_OUT_CLOSE_TOAST);
          yield put({ type: REQUEST_ADD_RESPONDER_SUCCESS, contactGroupId });
          history.push(`/responders/${id}`);
        } else {
          showError(res.data);
          yield put({
            type: REQUEST_ADD_RESPONDER_FAILED,
            error: res.data,
            contactGroupId,
          });
        }
      }
    } else {
      setSubmitting(true);
      if (isResponseSuccess(res.status)) {
        if (typeof res.data === 'object') {
          const id = res.data.id;
          Notification('success', 'Build new responder successfully!', '', TIME_OUT_CLOSE_TOAST);
          setSubmitting && setSubmitting(false);
          yield put({ type: REQUEST_ADD_RESPONDER_SUCCESS, contactGroupId });
          history.push(`/responders/${id}`);
        } else {
          showError(res.data);
          setSubmitting && setSubmitting(false);
          yield put({
            type: REQUEST_ADD_RESPONDER_FAILED,
            error: res.data,
            contactGroupId,
          });
        }
      }
    }
  } catch (error) {
    if (error.response.status === 400) {
      if (error.response.data && error.response.data.body) {
        showError(error.response.data.body);
      } else {
        showError('Can not create another dialog for same contact group!');
      }
      setSubmitting && setSubmitting(false);
      yield put({
        type: REQUEST_ADD_RESPONDER_FAILED,
        error,
        contactGroupId,
      });
    } else {
      showError(error);
      setSubmitting && setSubmitting(false);
      yield put({
        type: REQUEST_ADD_RESPONDER_FAILED,
        error,
        contactGroupId,
      });
    }
    callbacks && callbacks.onFailed(error);
  }
}

function* getResponderDetail(data) {
  const { id } = data;
  try {
    const resDetailDialog = yield Services.post(`${LOAD_DIALOG_DETAIL_URL}?id=${id}`);

    if (resDetailDialog.data) {
      const item = { ...resDetailDialog.data, children: resDetailDialog?.data?.dialogContents };
      yield put({
        type: REQUEST_GET_RESPONDER_DETAIL_SUCCESS,
        data: item,
      });
    } else {
      yield put({
        type: REQUEST_GET_RESPONDER_DETAIL_FAILED,
        error: '[GET_RESPONDER_DETAIL] Something went wrong!',
      });
      showError('[GET_RESPONDER_DETAIL] Something went wrong!');
    }
  } catch (error) {
    showError(error);
    yield put({
      type: REQUEST_GET_RESPONDER_DETAIL_FAILED,
      error,
    });
  }
}

function* saveResponderContent(data) {
  const { isNew, isClone, resetForm, callbacks, payload } = data;
  try {
    const body = ResponderModel.parseBodyToSaveNode(payload, { isNew, isClone });
    //! Add new
    if (isNew) {
      const res = yield Services.post(SAVE_DIALOG_CONTENT_URL, body);
      if (isResponseSuccess(res.status)) {
        if (typeof res.data === 'object') {
          ScrollTopPosition(0);
          Notification('success', 'Add new node Successfully!', '', TIME_OUT_CLOSE_TOAST);
          yield put({
            type: REQUEST_SAVE_RESPONDER_CONTENT_SUCCESS,
            newNodeAdded: res.data,
          });
          callbacks && callbacks.onSuccess(res);
          resetForm && resetForm();
        }
      }
    }

    //! Clone
    else if (isClone) {
      const res = yield Services.post(SAVE_DIALOG_CONTENT_URL, body);
      if (isResponseSuccess(res.status)) {
        if (typeof res.data === 'object') {
          ScrollTopPosition(0);
          Notification('success', 'Clone node successfully!', '', TIME_OUT_CLOSE_TOAST);
          yield put({
            type: REQUEST_SAVE_RESPONDER_CONTENT_SUCCESS,
            newNodeClone: res.data,
          });
          callbacks && callbacks.onSuccess(res);
          resetForm && resetForm();
        }
      }
    }

    //! Update
    else {
      const res = yield Services.post(SAVE_DIALOG_CONTENT_URL, body);
      if (isResponseSuccess(res.status)) {
        if (typeof res.data === 'object') {
          ScrollTopPosition(0);
          Notification('success', 'Save node Successfully!', '', TIME_OUT_CLOSE_TOAST);
          yield put({
            type: REQUEST_SAVE_RESPONDER_CONTENT_SUCCESS,
            selectNodeUpdated: res.data,
          });
          resetForm && resetForm();
          callbacks && callbacks.onSuccess(res);
        }
      }
    }
  } catch (error) {
    showError(error);
    callbacks && callbacks.onFailed(error);
    yield put({
      type: REQUEST_SAVE_RESPONDER_CONTENT_FAILED,
      error,
    });
  }
}

function* uploadNodes(data) {
  const { idResponder, file, setSubmitting, toggle } = data;
  try {
    const formData = new FormData();
    formData.append('dialogFile', file);
    const res = yield Services.post(`${UPLOAD_DIALOG_FILE_URL}?dialogId=${idResponder}`, formData);
    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'object') {
        yield put({ type: REQUEST_UPLOAD_NODE_SUCCESS });
        yield getResponderDetail({ id: idResponder });
        Notification('success', 'Import Node Successfully!', '', TIME_OUT_CLOSE_TOAST);
        setSubmitting(false);
        toggle();
      }
    }
  } catch (error) {
    showError(error);
    setSubmitting(false);
    toggle();
    yield put({
      type: REQUEST_UPLOAD_NODE_FAILED,
      error,
    });
  }
}

function* deleteDialogContent(data) {
  const { id, idResponder, toggle, setSubmitting, softDelete, callbacks } = data;
  setSubmitting && setSubmitting(true);

  if (softDelete) {
    try {
      const res = yield Services.delete(
        `${SOFT_DELETE_DIALOG_CONTENT_URL}?dialogId=${idResponder}&nodeId=${id}`
      );

      console.log('dqoiwdjoiqwjd', res);

      if (res?.data === 1) {
        callbacks && callbacks.onSuccess();
        yield put({
          type: REQUEST_DELETE_RESPONDER_CONTENT_SUCCESS,
          id,
          softDelete: true,
        });
      } 
    } catch (error) {
      setSubmitting && setSubmitting(false);
      toggle && toggle();
      callbacks && callbacks.onFailed();
      showError(error);
    }
    return;
  }

  try {
    const res = yield Services.post(`${DELETE_DIALOG_CONTENT_URL}?id=${id}`);
    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'number' && res.data === 1) {
        Notification('success', 'Deleted dialog Successfully!', '', TIME_OUT_CLOSE_TOAST);
        setSubmitting && setSubmitting(true);
        toggle && toggle();
        callbacks && callbacks.onSuccess();
        yield put({
          type: REQUEST_DELETE_RESPONDER_CONTENT_SUCCESS,
          id,
        });
      } else {
        showError('Something went wrong!');
        setSubmitting && setSubmitting(false);
        toggle && toggle();
        callbacks && callbacks.onFailed();
        yield put({
          type: REQUEST_DELETE_RESPONDER_CONTENT_FAILED,
          error: 'Something went wrong!',
        });
      }
    }
  } catch (error) {
    showError(error);
    setSubmitting && setSubmitting(false);
    toggle && toggle();
    callbacks && callbacks.onFailed();
    yield put({
      type: REQUEST_DELETE_RESPONDER_CONTENT_FAILED,
      error,
    });
  }
}

function* setDidGroupInResponder({ callbacks, ...data }) {
  try {
    const response = yield ResponderServices.setDidResponder(
      ResponderModel.parseBodyToSaveResponderDetail(data)
    );

    yield put({
      type: REQUEST_SET_DID_GROUP_RESPONDER_SUCCESS,
      data: response.data,
    });
    Notification('success', 'Set DID Group Successfully!', '', TIME_OUT_CLOSE_TOAST);
    callbacks && callbacks.onSuccess(response);
  } catch (error) {
    showError(error);
    callbacks && callbacks.onFailed(error);
    yield put({
      type: REQUEST_SET_DID_GROUP_RESPONDER_FAILED,
      error,
    });
  }
}

function* loadMoreDialog(data) {
  const { filters, published } = data;

  // parse data to request
  const parseFilters = cloneDeep(filters);
  parseFilters.status = parseFilters?.status?.value;
  parseFilters.contactGroupName = parseFilters?.contactGroupName?.name;
  try {
    const res = yield ResponderServices.loadMoreResponder({ published, filters: parseFilters });
    if (res) {
      yield put({
        type: REQUEST_LOAD_MORE_RESPONDER_SUCCESS,
        data: res.data,
        published,
        isLast: checkIsLastResponder(res.data),
      });
    }
  } catch (error) {
    showError(error);
    yield put({
      type: REQUEST_LOAD_MORE_RESPONDER_FAILED,
      error,
    });
  }
}

function* filterResponder(data) {
  const { filters, published } = data;

  // parse data to request
  const parseFilters = cloneDeep(filters);
  parseFilters.status = parseFilters?.status?.value;
  parseFilters.contactGroupName = parseFilters?.contactGroupName?.name;

  try {
    const res = yield ResponderServices.getListDialogs({ published, filters: parseFilters });
    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'object') {
        yield put({
          type: REQUEST_FILTER_RESPONDER_SUCCESS,
          data: res.data,
          published,
          isLast: checkIsLastResponder(res.data),
        });
      }
      if (typeof res.data === 'string') {
        const message = res.data.toLowerCase();
        if (message === 'fail') {
          showError(message, 'Error');
          yield put({
            type: REQUEST_FILTER_RESPONDER_FAILED,
            error: message,
            published,
          });
        }
      }
    }
  } catch (error) {
    showError(error);
    yield put({
      type: REQUEST_FILTER_RESPONDER_FAILED,
      error,
      published,
    });
  }
}

export default function* watchAPIFunction() {
  yield takeLatest(REQUEST_GET_LIST_DIALOG, getListDialog);
  yield takeLatest(REQUEST_GET_LIST_DIALOG_TEMPLATE, getListDialogTemplate);
  yield takeLatest(REQUEST_SAVE_DIALOG, updateDialog);
  yield takeEvery(REQUEST_SWITCH_RESPONDER, switchResponder);
  yield takeEvery(REQUEST_DELETE_RESPONDER, deleteResponder);
  yield takeLatest(REQUEST_ADD_RESPONDER, createResponder);
  yield takeLatest(REQUEST_GET_RESPONDER_DETAIL, getResponderDetail);
  yield takeLatest(REQUEST_SAVE_RESPONDER_CONTENT, saveResponderContent);
  yield takeLatest(REQUEST_UPLOAD_NODE, uploadNodes);
  yield takeLatest(REQUEST_DELETE_RESPONDER_CONTENT, deleteDialogContent);
  yield takeLatest(REQUEST_SET_DID_GROUP_RESPONDER, setDidGroupInResponder);
  yield takeLatest(REQUEST_LOAD_MORE_RESPONDER, loadMoreDialog);
  yield takeLatest(REQUEST_FILTER_RESPONDER, filterResponder);
}
